// src/core/utils/cropImage.js

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // Needed to avoid cross-origin issues for some images
    image.src = url;
  });

/**
 * This function was adapted from https://codesandbox.io/s/react-easy-crop-demo-with-getting-cropped-image-blob-for-upload-yq8px
 *
 * @param {string} imageSrc - The image URL
 * @param {Object} pixelCrop - Pixel crop area (from onCropComplete)
 * @param {string} fileName - Desired filename for the output File object
 * @returns {Promise<File>} - A Promise that resolves with a File object of the cropped image
 */
export async function getCroppedImg(imageSrc, pixelCrop, fileName) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context available for canvas');
  }

  // Calculate the scale to draw the image at its natural resolution
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  // Set canvas size to match the cropped area size
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped portion of the image onto the canvas
  // The first 5 arguments are for the source image (x, y, width, height)
  // The last 4 arguments are for the destination canvas (x, y, width, height)
  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Return the cropped image as a File object
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Canvas is empty or blob conversion failed');
        return reject(new Error('Canvas is empty or blob conversion failed'));
      }
      // Create a File object from the blob
      // You can customize the mime type and quality if needed
      const file = new File([blob], fileName, { type: 'image/jpeg' }); // Defaulting to JPEG
      resolve(file);
    }, 'image/jpeg', 0.95); // 0.95 is the quality (0 to 1), adjust as needed
  });
}